import {
    COLLAPSED_SPORTS_PATH,
    LS_FILTER_SHOW_CASHIN,
    LOCAL_STORAGE_FILTER_RADAR,
    LOCAL_STORAGE_FILTER_STREAM,
    LOCAL_STORAGE_SORT,
    LOCAL_STORAGE_TUTORIAL,
    SESSION_STORAGE_TOURNAMENTS,
    SORT_BY_TOURNAMENTS
} from './constants';
import { dispatch } from 'nike-re-framejs';
import { showAllCashins } from './components/cashin/handlers';
import { fromJS, Set } from 'immutable';
import { toggleOneTournament } from '../../common/util';
import { sportKey } from './utils';

export const initTurboLive = db => {
    const defaultSort = localStorage.getItem(LOCAL_STORAGE_SORT) || SORT_BY_TOURNAMENTS;
    const streamFilter = localStorage.getItem(LOCAL_STORAGE_FILTER_STREAM) === 'true' || false;
    const radarFilter = localStorage.getItem(LOCAL_STORAGE_FILTER_RADAR) === 'true' || false;
    const defaultCashin = localStorage.getItem(LS_FILTER_SHOW_CASHIN) === 'true';
    return db
        .setIn(['turboLive', 'sortBy'], defaultSort)
        .setIn(['turboLive', 'filter', 'stream'], streamFilter)
        .setIn(['turboLive', 'filter', 'radar'], radarFilter)
        .setIn(['turboLive', 'filter', 'showCashin'], defaultCashin);
};

export const clearTurboLive = db => {
    return db.delete('turboLive');
};

export const setFilterSport = sport => db => {
    const oldFilterSport = db.getIn(['turboLive', 'filter', 'sport']);
    let oldScrollTop = db.getIn(['turboLive', 'scrollTop'], 0);
    return oldFilterSport === sport
        ? db
        : db.setIn(['turboLive', 'filter', 'sport'], sport).setIn(['turboLive', 'scrollTop'], ++oldScrollTop);
};

export const toggleFilterStream = () => db => {
    const oldFilterStream = db.getIn(['turboLive', 'filter', 'stream']);
    localStorage.setItem(LOCAL_STORAGE_FILTER_STREAM, String(!oldFilterStream));

    let oldScrollTop = db.getIn(['turboLive', 'scrollTop'], 0);
    return db
        .setIn(['turboLive', 'filter', 'stream'], !oldFilterStream)
        .setIn(['turboLive', 'scrollTop'], ++oldScrollTop);
};

export const toggleFilterRadar = db => {
    const oldFilterRadar = db.getIn(['turboLive', 'filter', 'radar']);
    localStorage.setItem(LOCAL_STORAGE_FILTER_RADAR, String(!oldFilterRadar));

    let oldScrollTop = db.getIn(['turboLive', 'scrollTop'], 0);
    return db
        .setIn(['turboLive', 'filter', 'radar'], !oldFilterRadar)
        .setIn(['turboLive', 'scrollTop'], ++oldScrollTop);
};

export const setSortBy = sortBy => db => {
    localStorage.setItem(LOCAL_STORAGE_SORT, sortBy);
    return db.setIn(['turboLive', 'sortBy'], sortBy);
};

export const resetFilter = db => {
    let newDb = db;
    const showCashin = db.getIn(['turboLive', 'filter', 'showCashin']);
    if (!showCashin) {
        newDb = showAllCashins(db);
    }
    localStorage.removeItem(LOCAL_STORAGE_SORT);
    localStorage.removeItem(LOCAL_STORAGE_FILTER_STREAM);
    localStorage.removeItem(LOCAL_STORAGE_FILTER_RADAR);
    return newDb
        .setIn(['turboLive', 'filter', 'stream'], false)
        .setIn(['turboLive', 'filter', 'radar'], false)
        .setIn(['turboLive', 'sortBy'], SORT_BY_TOURNAMENTS);
};

export const setActiveTicketsTab = db => {
    let old = db.getIn(['turboLive', 'detail', 'ticketTabMark']) || 0;
    return db.setIn(['turboLive', 'detail', 'ticketTabMark'], ++old);
};

export const setActiveAnalysisTab = db => {
    return db.setIn(['turboLive', 'detail', 'analysisTabMark'], true);
};

export const setDetailMiniScoreVisible = (visible: boolean) => db => {
    return db.setIn(['turboLive', 'detail', 'miniScoreVisible'], visible);
};

export const setDetailMiniTimerVisible = (visible: boolean) => db => {
    return db.setIn(['turboLive', 'detail', 'miniDetailVisible'], visible);
};

export const resetActiveTicketsTab = db => db.setIn(['turboLive', 'detail', 'ticketTabMark'], 0);

export const resetActiveAnalysisTab = db => db.deleteIn(['turboLive', 'detail', 'analysisTabMark']);

export const setSelectedMarketIds = (sportName, selectedMarketIds) => db =>
    db.setIn(['turboLive', 'selectedMarketIds', sportName], selectedMarketIds);

export const setFirstMarketId = (sportName, marketId) => db =>
    db.updateIn(['turboLive', 'selectedMarketIds', sportName], existing =>
        existing?.size && marketId ? existing.set(0, marketId) : existing
    );

export const setOverviewScrollY = scrollY => db => db.setIn(['turboLive', 'overviewScrollY'], scrollY);

export const setTutorialStatus = status => db => db.setIn(['turboLive', 'tutorial', 'completed'], status);

export const setTutorialCompletionStatus = db => {
    const tutorialCompleted = localStorage.getItem(LOCAL_STORAGE_TUTORIAL) === 'completed';
    dispatch(setTutorialStatus(tutorialCompleted));
    return db;
};

export const tournamentsCollapsedInit = sportName => db => {
    let sessionTournaments = JSON.parse(sessionStorage.getItem(SESSION_STORAGE_TOURNAMENTS));
    // 1. session storage not initialized
    if (sessionTournaments === null) sessionTournaments = {};
    // 2. session storage initialized, but sport missing
    if (!sessionTournaments[sportName])
        sessionTournaments[sportName] = { collapsedAll: false, customTournamentsIds: {} };

    sessionStorage.setItem(SESSION_STORAGE_TOURNAMENTS, JSON.stringify(sessionTournaments));
    return db.setIn(['turboLive', 'collapsedTournaments'], fromJS(sessionTournaments));
};

export const toggleTournament = (tournamentId, sportName) => db =>
    toggleOneTournament(
        db,
        tournamentId,
        ['turboLive', 'collapsedTournaments'],
        SESSION_STORAGE_TOURNAMENTS,
        sportName
    );

// 1. Remove all custom tournaments in session storage
// 2. Set collapsedAll -> true/false in session storage
// 3. Set in reframe
export const toggleAllTournaments = sportName => db => {
    let sessionTournaments = db.getIn(['turboLive', 'collapsedTournaments']).toJS();
    sessionTournaments[sportName].customTournamentsIds = {};
    sessionTournaments[sportName].collapsedAll = !sessionTournaments[sportName].collapsedAll;
    sessionStorage.setItem(SESSION_STORAGE_TOURNAMENTS, JSON.stringify(sessionTournaments));
    return db.setIn(['turboLive', 'collapsedTournaments'], fromJS(sessionTournaments));
};

export const toggleSport = (sportName: string, key: string) => db => {
    const collapsedSportKey = sportKey(sportName, key);
    let collapsedSports = db.getIn(COLLAPSED_SPORTS_PATH, Set());
    collapsedSports = collapsedSports.includes(collapsedSportKey)
        ? collapsedSports.delete(collapsedSportKey)
        : collapsedSports.add(collapsedSportKey);
    return db.setIn(COLLAPSED_SPORTS_PATH, collapsedSports);
};

export const removeSelectedMarketCategory = db => db.removeIn(['turboLive', 'detail', 'selectedMarketCategoryId']);
